import type React from 'react';
import type { JobCost, JobCostingDashboardProps } from '@/app/client-zone/modules/total-operations/components/manufacturing/JobCostingDashboard';
import type { StatCard } from '@/components/modules/ModuleHeader';
import type { BillingSubmissionStatus } from '../work-items/billingSubmissionTypes';

export type BillingDashboardScopeType = 'appointment' | 'task' | 'job' | 'project' | 'aggregate';
export type BillingSectionId = 'ledger' | 'costing' | 'time' | 'expenses' | 'maintenance' | 'submissions';
export type JobBillingPrimarySectionId = 'ledger' | 'costing' | 'time' | 'expenses' | 'submissions';

export interface TimeSummary {
  total_entries: number;
  total_duration_hours: number;
  billable_duration_hours: number;
  non_billable_duration_hours: number;
  billable_amount?: number;
  total_hours_formatted?: string;
  related_items_count?: number;
  travel_hours: number;
  travel_distance_km: number;
  onsite_hours: number;
  travel_entries_count?: number;
  onsite_entries_count?: number;
}

export interface ProjectTimeSummary {
  total_hours: number;
  billable_hours: number;
  non_billable_hours?: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entry_count: number;
  jobs_count: number;
  people_count: number;
}

export interface BomItem {
  id: string;
  item_name?: string;
  description?: string;
  unit?: string;
  total_cost?: number;
  line_total?: number;
  quantity: number;
  unit_cost?: number;
  item_code?: string;
  item_id?: string;
  line_type?: string;
  tax_rate?: number;
  discount_percentage?: number;
  notes?: string;
  source_work_item_id?: string;
  source_type?: string;
  source_item_number?: string;
  source_title?: string;
}

export interface JobExpense {
  id: string;
  grand_total?: number;
  amount?: number;
  title?: string;
  description?: string;
  expense_category?: string;
  item_number?: string;
  item_type?: string;
  currency?: string;
}

export interface ProjectExpenseSummary {
  total_amount: number;
  expense_count: number;
}

export interface BillingRatesSummary {
  labour_rate: number;
  travel_time_rate: number;
  travel_km_rate: number;
  seeded: boolean;
}

/**
 * Billing rules configuration for automatic charge calculations
 */
export interface BillingRules {
  /** Minimum travel time (in hours) before charging - default 0 (always charge) */
  min_travel_time_hours: number;
  /** Minimum travel distance (in km) before charging - default 0 (always charge) */
  min_travel_distance_km: number;
  /** Flat call-out fee per appointment */
  call_out_fee_per_appointment: number;
  /** Whether to charge call-out fee for each appointment (true) or per job (false) */
  charge_call_out_per_appointment: boolean;
  /** Per-user labour rates (user_id -> hourly rate) - overrides default labour_rate */
  user_labour_rates: Record<string, number>;
  /** Whether rules have been applied/synchronized */
  is_synced: boolean;
}

export interface TeamScopeSnapshot {
  total_appointments: number;
  team_assigned_appointments: number;
  team_names: string[];
}

export interface MaintenanceRecord {
  id: string;
  cost?: number;
  next_due_date?: string;
}

export interface EffectiveJobStatusDetails {
  status?: string;
  sourceStatus?: string;
  appointmentCount: number;
  activeAppointmentCount: number;
  derivedFromAppointments: boolean;
}

export type BudgetInsightCategory = 'labor' | 'materials' | 'fuel' | 'equipment' | 'subcontractors' | 'other';

export interface BudgetInsightBreakdownItem {
  category: BudgetInsightCategory;
  estimated: number;
  income: number;
  expenses: number;
  net: number;
}

export interface JobFinancialInsights {
  health: string;
  time_summary: {
    by_person: Array<{
      user_id: number;
      user_name: string;
      total_hours: number;
      billable_hours: number;
      travel_hours: number;
      onsite_hours: number;
      billable_ratio: number;
      entries_count: number;
      work_items_count: number;
      appointments_count: number;
    }>;
  };
  profitability: {
    total_budget: number;
    total_revenue: number;
    total_expenses: number;
    net_profit: number;
    net_margin_pct: number;
    budget_variance: number;
    budget_variance_pct: number;
    is_over_budget: boolean;
    category_breakdown: BudgetInsightBreakdownItem[];
  };
  source_breakdown: Array<{
    id: string;
    item_number: string;
    title: string;
    item_type: 'job' | 'appointment';
    status: string;
    assigned_to_name: string | null;
    assigned_team_name?: string | null;
    assigned_users: string[];
    time: {
      total_hours: number;
      billable_hours: number;
      travel_hours: number;
      onsite_hours: number;
      travel_distance_km: number;
      entries_count: number;
    };
    billing: {
      bom_total: number;
      time_billing_total: number;
      total: number;
    };
    expenses: {
      direct_total: number;
      time_cost_total: number;
      total: number;
      count: number;
    };
    profitability: {
      net_profit: number;
      margin_pct: number;
    };
  }>;
  documents: Array<{
    id: string;
    item_type: string;
    item_number: string;
    title: string;
    status: string;
    total_amount: number;
    created_at: string;
    return_reason?: string | null;
    last_review_notes?: string | null;
    linked_items: Array<{ id: string; item_number: string; item_type: 'job' | 'appointment' }>;
  }>;
}

export interface BillingSubmissionSummary {
  id: string;
  item_number: string;
  status: BillingSubmissionStatus;
  created_at: string;
  requested_doc_type: 'invoice' | 'quote' | 'proforma';
  currency: string;
  notes: string;
  billing_settings?: {
    bill_travel_time?: boolean;
    bill_travel_km?: boolean;
    bill_onsite_labour?: boolean;
  } | null;
  timer_aggregation_mode?: 'individual' | 'team' | null;
  line_items: Array<{
    id?: string;
    item_name?: string;
    description?: string;
    quantity: number;
    unit?: string;
    unit_cost: number;
    line_total?: number;
  }>;
}

export type BillingWorkspaceItem = {
  id: string;
  item_number?: string;
  title?: string;
  item_type?: string;
  item_subtype?: string;
  contact_id?: number;
  contact_name?: string;
  status?: string;
  assigned_to?: number | string;
  assigned_to_name?: string;
  assignee_name?: string;
  custom_fields?: Record<string, unknown>;
  assignees?: Array<{
    user_id?: number | string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }>;
  location_id?: number;
  location_name?: string;
  location_address?: string;
  contact_person_id?: number;
  contact_person_name?: string;
};

export const budgetHealthMeta: Record<string, { dot: string; label: string; pillClass: string }> = {
  on_track: {
    dot: 'bg-green-500',
    label: 'On track',
    pillClass: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800/60 dark:bg-green-950/30 dark:text-green-300',
  },
  profitable: {
    dot: 'bg-emerald-500',
    label: 'Profitable',
    pillClass: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-300',
  },
  needs_attention: {
    dot: 'bg-yellow-500',
    label: 'Needs attention',
    pillClass: 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800/60 dark:bg-yellow-950/30 dark:text-yellow-300',
  },
  at_risk: {
    dot: 'bg-red-500',
    label: 'At risk',
    pillClass: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-300',
  },
};

export interface BillingDashboardProps {
  permissions?: JobCostingDashboardProps['permissions'];
  templateKey?: string;
  itemTypes?: string | string[];
  workItemId?: string;
  workItemType?: string;
  rootItemIds?: string[] | string;
  includeDescendants?: boolean;
  title?: string;
  subtitle?: string;
  embedded?: boolean;
  showHeaderControls?: boolean;
  onOpenItem?: (item: JobCost) => void | Promise<void>;
  /** When provided, the module-level costing summary cards are reported to the
   *  host (shared StrictModuleHeader Row C) instead of rendering inline. */
  onStatsChange?: (cards: StatCard[]) => void;
  isActive?: boolean;
  includeRelated?: boolean;
  readOnly?: boolean;
  workItem?: BillingWorkspaceItem;
  defaultSection?: BillingSectionId;
  onExpensesChange?: (count: number) => void;
  /** When false on a job, only timers are available (other billing workspace sections hidden). Owner/root should pass true from the shell. */
  jobBillingWorkspaceAccess?: boolean;
  /**
   * When false on a job, the ledger hides Tax Invoice and blocks generating invoices (quotes/proformas still allowed).
   * When omitted, invoice generation stays available for backward-compatible embeds; work-item modal passes the resolved RBAC flag.
   */
  operationsJobGenerateInvoiceAccess?: boolean;
  /**
   * When false, timer entries in the billing workspace are read-only (SmartTask timers; sign-off and edits).
   * Controlled by team setting `operations_job_billing_timers_signoff`. Owner/root should pass true from the shell.
   */
  timersSignoffAccess?: boolean;
  /**
   * When false, billing lines and expenses are read-only (cannot finalize billing).
   * Owner/root should pass true from the shell.
   */
  billingFinalizeAccess?: boolean;
  /**
   * Process gate status for checking if job can be closed.
   * If is_complete is true and is_blocked is false, the job can be closed.
   */
  processGateStatus?: {
    is_complete: boolean;
    is_blocked: boolean;
  } | null;
  /**
   * Function to update the work item status (e.g., to 'closed').
   * Used when user confirms job closure after billing request creation.
   */
  onUpdateWorkItemStatus?: (status: string) => Promise<void>;
  /**
   * Function to open the DocumentModal for the created billing request.
   * Called after successful billing request creation and optional job closure.
   */
  onOpenDocumentModal?: (docId: string, docType: string) => void;
  /** Fired when the user wants to create a new expense linked to this work item. */
  onCreateExpense?: (prefill?: { job_id?: string; work_item_id?: string }) => void;
  /**
   * When provided, controls the ledger-generated state externally (controlled mode).
   * When true, the billable ledger tab is visible and timers tab is hidden for job workspaces.
   */
  ledgerGenerated?: boolean;
  /** Fired when the user clicks "Generate Billing Ledger". Parent should set ledgerGenerated to true. */
  onLedgerGenerated?: () => void;
  /** Operations billing strictness mode — controls whether review/approval steps are required. */
  billingStrictness?: 'simple' | 'standard' | 'strict';
  /** Whether the user can submit for billing conversion (RBAC: submit_for_billing). */
  canSubmitForBilling?: boolean;
  /** Whether the user can review billing (RBAC: review_billing, Standard/Strict modes). */
  canReviewBilling?: boolean;
  /** Whether the user can approve billing (RBAC: approve_billing, Strict mode). */
  canApproveBilling?: boolean;
  /**
   * When set, triggers handleGenerateBilling with the specified doc type.
   * Used by the parent (WorkItemDetailModal header dropdown) to initiate BLS generation
   * with a specific document type (quote, proforma, or invoice).
   * The parent should change this value each time it wants to trigger generation.
   */
  pendingDocType?: 'quote' | 'proforma' | 'invoice' | null;
  /**
   * Fired when the user changes the timer aggregation mode. The dashboard already
   * persists it to the work item; the parent may mirror it into local work-item
   * state so previews/downloads use the new value immediately.
   */
  onTimerAggregationModeChange?: (mode: 'individual' | 'team') => void;
}

export interface DashboardPanelProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  isDark: boolean;
  actions?: React.ReactNode;
}

export interface DashboardStatCardProps {
  label: string;
  value: string;
  detail?: string;
  icon: React.ElementType;
  isDark: boolean;
  accentColor: string;
  valueClassName?: string;
}
